import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Inbox, LogOut, Mail, Phone, Trash2, RefreshCw, Filter, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  nieuw: "Nieuw",
  in_behandeling: "In behandeling",
  afgehandeld: "Afgehandeld",
};

const TYPE_LABEL = {
  contact: "Contact",
  hulp: "Hulpaanvraag",
  steun: "Steun / donatie",
  vrijwilliger: "Vrijwilliger",
  donateur: "Donateur",
  partner: "Partner",
  voor_ouders: "Ouder",
};

const TYPE_COLORS = {
  contact: "bg-sand text-petrol border-line",
  hulp: "bg-gold text-petrol border-gold",
  steun: "bg-goldSoft text-petrol border-gold/50",
  vrijwilliger: "bg-goldSoft text-petrol border-line",
  donateur: "bg-gold/20 text-petrol border-gold/30",
  partner: "bg-petrol text-cream border-petrol",
  voor_ouders: "bg-gold text-petrol border-gold",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");
  const [typeFilter, setTypeFilter] = useState("alle");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/contact"),
        api.get("/contact/stats"),
      ]);
      setItems(list);
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onUpdateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/contact/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Status bijgewerkt: ${STATUS_LABEL[status]}`);
      const { data: s } = await api.get("/contact/stats");
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Weet u zeker dat u deze aanvraag wilt verwijderen?")) return;
    try {
      await api.delete(`/contact/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Aanvraag verwijderd");
      const { data: s } = await api.get("/contact/stats");
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const filtered = items.filter((i) => {
    if (filter !== "alle" && i.status !== filter) return false;
    if (typeFilter !== "alle" && (i.type || "contact") !== typeFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            <img src="/logo-192.png" alt="FIN" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-display font-bold text-ink text-sm leading-tight">Stichting FIN</p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">Beheer</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-100 text-ink font-semibold text-sm" data-testid="admin-nav-aanvragen">
            <Inbox size={17} /> Aanvragen
          </button>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Ingelogd als</p>
          <p className="text-sm font-semibold text-ink truncate">{user?.email}</p>
          <button
            onClick={logout}
            className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-semibold text-slate-700 hover:text-terra hover:bg-slate-100 px-3 py-2 rounded-lg transition"
            data-testid="admin-logout-btn"
          >
            <LogOut size={15} /> Uitloggen
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 md:px-8 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-xl md:text-2xl text-ink">Aanvragen</h1>
            <p className="text-xs text-slate-500 mt-0.5">Overzicht van berichten uit alle formulieren</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-100 transition"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={15} /> <span className="hidden sm:inline">Vernieuwen</span>
            </button>
            <button onClick={logout} className="md:hidden inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="admin-logout-mobile-btn">
              <LogOut size={15} />
            </button>
          </div>
        </header>

        <div className="p-6 md:p-8 flex-1">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats-grid">
            <StatCard label="Totaal" value={stats?.total ?? "—"} />
            <StatCard label="Nieuw" value={stats?.nieuw ?? "—"} accent />
            <StatCard label="In behandeling" value={stats?.in_behandeling ?? "—"} />
            <StatCard label="Afgehandeld" value={stats?.afgehandeld ?? "—"} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="admin-filters">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 mr-2">
              <Filter size={13} /> Status:
            </span>
            {["alle", "nieuw", "in_behandeling", "afgehandeld"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                  filter === f
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`admin-filter-${f}`}
              >
                {f === "alle" ? "Alle" : STATUS_LABEL[f]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 mr-2">Type:</span>
            {["alle", "contact", "hulp", "steun", "voor_ouders", "vrijwilliger", "donateur", "partner"].map((f) => (
              <button
                key={f}
                onClick={() => setTypeFilter(f)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                  typeFilter === f
                    ? "bg-terra text-white border-terra"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
                data-testid={`admin-type-filter-${f}`}
              >
                {f === "alle" ? "Alle" : TYPE_LABEL[f]}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className={selected ? "lg:col-span-7" : "lg:col-span-12"}>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center text-slate-500 text-sm">Laden…</div>
                ) : filtered.length === 0 ? (
                  <div className="p-16 text-center" data-testid="admin-empty-state">
                    <Inbox size={36} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600 font-semibold">Geen aanvragen</p>
                    <p className="text-slate-400 text-sm mt-1">Nog geen aanvragen in deze filter.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100" data-testid="admin-aanvragen-list">
                    {filtered.map((it) => {
                      const t = it.type || "contact";
                      return (
                        <li key={it.id}>
                          <button
                            onClick={() => setSelected(it)}
                            className={`w-full text-left px-5 py-4 hover:bg-slate-50 transition flex items-start gap-4 ${
                              selected?.id === it.id ? "bg-slate-50" : ""
                            }`}
                            data-testid={`admin-item-${it.id}`}
                          >
                            <StatusBadge status={it.status} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-3 flex-wrap">
                                <p className="font-semibold text-ink truncate">{it.naam}</p>
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${TYPE_COLORS[t] || TYPE_COLORS.contact}`}>
                                  {TYPE_LABEL[t] || t}
                                </span>
                                {it.organisatie && <span className="text-xs text-slate-500">{it.organisatie}</span>}
                              </div>
                              <p className="text-sm text-slate-600 truncate mt-0.5">{it.bericht}</p>
                              <p className="text-[11px] text-slate-400 mt-1">{new Date(it.created_at).toLocaleString("nl-NL")}</p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {selected && (
              <div className="lg:col-span-5">
                <div className="bg-white border border-slate-200 rounded-xl p-6 sticky top-6" data-testid="admin-detail-panel">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h2 className="font-display font-bold text-xl text-ink leading-tight">{selected.naam}</h2>
                      <p className="text-xs text-slate-500 mt-1">{new Date(selected.created_at).toLocaleString("nl-NL")}</p>
                    </div>
                    <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
                  </div>

                  <dl className="space-y-3 text-sm">
                    <Row label="Type" value={TYPE_LABEL[selected.type || "contact"]} />
                    {selected.organisatie && <Row label="Organisatie" value={selected.organisatie} />}
                    {selected.rol && <Row label="Rol" value={selected.rol} />}
                    {selected.gemeente && <Row label="Gemeente" value={selected.gemeente} />}
                    {selected.contact_voorkeur && <Row label="Voorkeur" value={selected.contact_voorkeur === "telefoon" ? "Per telefoon" : "Per e-mail"} />}
                    {selected.thema && <Row label="Thema" value={selected.thema} />}
                    <Row label="E-mail" value={
                      <a href={`mailto:${selected.email}`} className="text-terra hover:underline inline-flex items-center gap-1">
                        <Mail size={13} />{selected.email}
                      </a>
                    } />
                    {selected.telefoon && (
                      <Row label="Telefoon" value={
                        <a href={`tel:${selected.telefoon}`} className="text-terra hover:underline inline-flex items-center gap-1">
                          <Phone size={13} />{selected.telefoon}
                        </a>
                      } />
                    )}
                    {selected.gevonden_via && <Row label="Via" value={selected.gevonden_via} />}
                  </dl>

                  <div className="mt-5 p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Bericht</p>
                    <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap" data-testid="admin-detail-message">{selected.bericht}</p>
                  </div>

                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Status</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(STATUS_LABEL).map(([k, v]) => (
                        <button
                          key={k}
                          onClick={() => onUpdateStatus(selected.id, k)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                            selected.status === k
                              ? "bg-ink text-white border-ink"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                          }`}
                          data-testid={`admin-status-${k}`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap gap-2 justify-between">
                    <a
                      href={`mailto:${selected.email}?subject=${encodeURIComponent("Stichting FIN — uw aanvraag")}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-terra hover:text-terraDark"
                      data-testid="admin-mailto-link"
                    >
                      <ExternalLink size={14} /> Beantwoord via e-mail
                    </a>
                    <button
                      onClick={() => onDelete(selected.id)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700"
                      data-testid="admin-delete-btn"
                    >
                      <Trash2 size={14} /> Verwijderen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`bg-white border rounded-xl p-5 ${accent ? "border-terra/30" : "border-slate-200"}`}>
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${accent ? "text-terra" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-4">
      <dt className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-24 shrink-0">{label}</dt>
      <dd className="text-sm text-ink flex-1 min-w-0 break-words">{value}</dd>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    nieuw: "bg-emerald-100 text-emerald-800 border-emerald-200",
    in_behandeling: "bg-amber-100 text-amber-800 border-amber-200",
    afgehandeld: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`mt-1 inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${map[status]} shrink-0`}>
      {STATUS_LABEL[status]}
    </span>
  );
}
